import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { FullStory } from '@fullstory/browser'
import { CheckOutlined, Error, HelpOutlineOutlined } from '@mui/icons-material'
import { Button, ClickAwayListener, Grid, IconButton, Typography, useTheme } from '@mui/material'
import { OpenFeature } from '@openfeature/react-sdk'
import { Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import Header from '../../components/alert-acknowledgement/header'
import LocationCard from '../../components/alert-acknowledgement/location-card'
import FormikTextField from '../../components/formik-input'
import ButtonGroup from '../../components/m3/button-group'
import PlainTooltip from '../../components/m3/plain-tooltip'
import NeedHelpModal from '../../components/need-help-modal'
import ScreenContainer from '../../components/screen'
import { isValidPhoneNumber } from '../../utils/helpers'
import useDates from '../../utils/hooks/useDates'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'

interface FormValues {
  login: string
  response: AcknowledgeType | undefined
}

const validateEmail = (email: string | undefined) => {
  return Yup.string().email().isValidSync(email)
}

const validatePhone = (phone: string | undefined) => {
  return Yup.string()
    .test(() => {
      return isValidPhoneNumber(phone)
    })
    .isValidSync(phone)
}

const AlertAcknowledge = () => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const code = params.code ?? searchParams.get('code')
  const navigate = useNavigate()
  const theme = useTheme()
  const sharedClasses = useSharedStyles
  const classes = useStyles
  const { formatDateTimeShortMonth, timezoneAbbr } = useDates()

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [inputError, setInputError] = useState<string>('')

  const [data, setData] = useState<GetAlarmsResponse | undefined>(undefined)

  const [showNeedHelpModal, setShowNeedHelpModal] = useState<boolean>(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const handleTooltipClose = () => {
    setTooltipOpen(false)
  }

  const handleTooltipOpen = () => {
    setTooltipOpen(true)
  }

  const initialValues = {
    login: '',
    response: undefined,
  } as FormValues

  const schema = Yup.object().shape({
    login: Yup.string()
      .required('Please enter a valid phone or email address')
      .test('email_or_phone', 'Please enter a valid phone or email address', (value) => {
        return validateEmail(value) || validatePhone(value)
      }),
    response: Yup.string().required('Please select your response'),
  })

  useEffect(() => {
    const fetchData = async () => {
      setError('')
      setLoading(true)
      try {
        const res = await fetch(`/api/v2/alarms/response/${code}`)
        const body = await res.json()

        // Set feature flag context with user information after login
        const evaluationContext = {
          targetingKey: String(body.userId),
          userId: String(body.userId),
        }
        await OpenFeature.setContext(evaluationContext)

        // Track user in FullStory
        if (body.userId) {
          FullStory('setIdentity', {
            uid: body.userId,
            properties: {
              userId: body.userId,
            },
          })
        }
        // Track event in FullStory
        FullStory('trackEvent', {
          name: 'Viewed Alert Acknowledgement Web',
          properties: {
            alert_type: body.reaction,
          },
        })

        if (body.message) {
          if (body.message === 'alarms_status code not found or expired') {
            setError('The alarm code you are using is either expired or does not exist')
          } else if (body.message === 'alarms_acknowledge code does not match required length') {
            setError('Invalid code length provided.')
          } else {
            setError(body.message)
          }
        } else if (body.type) {
          // If type is provided in the response (ack or test), the alert has already been acknowledged, navigate to next screen
          navigate(`/submit?type=${body.type}`, { state: { data: body } })
        } else {
          setData(body)
        }
      } catch {
        setError('Unknown error occurred')
      } finally {
        setLoading(false)
      }
    }

    if (code && code.length > 0) {
      fetchData()
    }
  }, [code, navigate])

  const submitResponse = async ({ login, response }: FormValues) => {
    const isPhone = isValidPhoneNumber(login)

    try {
      const payload = {
        code,
        // if the input text does not validate as a phone number, use it as an email
        email: !isPhone ? login : undefined,
        // if the input text validates as a phone number, use it as a phone
        phone: isPhone ? login : undefined,
        type: response,
      }
      const res = await fetch('/api/v2/alarms/response/acknowledge', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      const body = await res.json()

      if (body.message) {
        if (body.message === 'alarms_acknowledge matching user not found') {
          setInputError(
            `The ${isPhone ? 'number' : 'email'} you entered does not match our records. Please re-enter your ${isPhone ? 'phone number' : 'email address'}.`
          )
        } else {
          setInputError(body.message)
        }
      } else {
        // Set feature flag context with user information after login
        const evaluationContext = {
          targetingKey: String(body.userId),
          userId: String(body.userId),
        }
        OpenFeature.setContext(evaluationContext)
        // Send event to FullStory
        let responseProperty = ''
        if (response === 'ack') responseProperty = 'acknowledged'
        else if (response === 'test') responseProperty = 'ignored'
        else if (response === 'decline') responseProperty = 'declined'

        FullStory('trackEvent', { name: 'Responded Alert Acknowledgement Web', properties: { response: responseProperty, alert_type: data?.reaction } })

        navigate(`/submit?type=${response}`, { state: { data, phone: payload.phone, email: payload.email, code, supportLevel: body.supportLevel } })
      }
    } catch {
      setInputError('Unknown error occurred')
    }
  }

  const errorPage = !data || error

  const parseName = (name: string) => {
    const pipeIndex = name.indexOf('|')
    if (pipeIndex !== -1) {
      return name.substring(0, pipeIndex).trim()
    }
    return name
  }

  return (
    <ScreenContainer loading={loading} header={!errorPage ? <Header variant="red" /> : <></>}>
      <Grid container={true} sx={classes.container}>
        {!errorPage ? (
          <Grid container={true} size={12} sx={classes.contentContainer}>
            <Grid size={12} sx={classes.titleContainer}>
              {data.reaction && (
                <Typography variant="h5" sx={classes.titleText}>
                  {parseName(data.reaction)}
                </Typography>
              )}
              {data.alarmTs && (
                <Typography variant="caption">
                  {formatDateTimeShortMonth(data.alarmTs, true)} - {timezoneAbbr}
                </Typography>
              )}
            </Grid>

            <LocationCard deviceLocation={data?.metadata?.device_location} deviceLocationNote={data?.metadata?.device_location_note} />

            <Formik initialValues={initialValues} onSubmit={submitResponse} validationSchema={schema} validateOnMount={false}>
              {({ isValid, dirty, isSubmitting, values, errors, touched, setFieldValue }: FormikProps<FormValues>) => (
                <Form style={classes.form}>
                  <Grid container={true} sx={classes.formContentContainer}>
                    <Grid container={true} size={12} sx={classes.formSection}>
                      <Grid container={true} size={12} sx={classes.sectionHeader}>
                        <Typography variant="subtitle1" sx={classes.sectionTitle}>
                          Action Required: Check the Location
                        </Typography>

                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <PlainTooltip
                            title="Select “Ignore” if no further action is needed (ie. test or known issue). This response will be visible to all notification recipients."
                            open={tooltipOpen}
                            onClose={handleTooltipClose}
                          >
                            <IconButton onClick={handleTooltipOpen}>
                              <HelpOutlineOutlined />
                            </IconButton>
                          </PlainTooltip>
                        </ClickAwayListener>
                      </Grid>

                      <Grid size={12}>
                        <ButtonGroup fullWidth={true}>
                          <ButtonGroup.Button
                            selected={values.response === 'test'}
                            icon={values.response === 'test' ? <CheckOutlined fontSize="small" /> : undefined}
                            onClick={() => setFieldValue('response', 'test')}
                          >
                            Ignore
                          </ButtonGroup.Button>
                          <ButtonGroup.Button
                            selected={values.response === 'ack'}
                            icon={values.response === 'ack' ? <CheckOutlined fontSize="small" /> : undefined}
                            onClick={() => setFieldValue('response', 'ack')}
                          >
                            I’m on it
                          </ButtonGroup.Button>
                        </ButtonGroup>
                      </Grid>
                    </Grid>

                    <Grid>
                      <Grid container={true} size={12} sx={classes.detailsContainer}>
                        <Typography variant="subtitle1" sx={classes.detailsTitle}>
                          Verify Your Account<sup style={{ color: theme.palette.error.main }}>*</sup>
                        </Typography>
                        <FormikTextField
                          name="login"
                          label="Email or phone"
                          className="fs-exclude"
                          sx={{ ...sharedClasses.input(theme), ...classes.loginInput(theme) }}
                          endAdornment={errors.login && touched.login ? <Error color="error" /> : undefined}
                          error={!!inputError}
                          helperText={inputError}
                        />
                      </Grid>
                    </Grid>
                    <Grid sx={classes.spacer} />
                    <Grid container={true} size={12} sx={classes.submitButtonContainer}>
                      <Button
                        type="submit"
                        size="large"
                        disableElevation={true}
                        fullWidth={true}
                        loading={isSubmitting}
                        disabled={!isValid || !dirty}
                        variant="contained"
                      >
                        Submit Response
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        ) : (
          <Grid container={true} size={12} sx={classes.successContainer}>
            <Typography variant="h3" sx={classes.successTitle}>
              Unable to load
            </Typography>
            <Typography variant="body2" sx={classes.successMessage}>
              Alert may be expired or does not exist
            </Typography>

            <Grid container={true} sx={classes.successButtonContainer}>
              <Button
                variant="contained"
                size="large"
                disableElevation={true}
                color="secondary"
                onClick={() => {
                  setShowNeedHelpModal(true)
                }}
              >
                Need more help?
              </Button>
            </Grid>

            <NeedHelpModal isOpen={showNeedHelpModal} onClose={() => setShowNeedHelpModal(false)} />
          </Grid>
        )}
      </Grid>
    </ScreenContainer>
  )
}

export default AlertAcknowledge
